import { motion } from "framer-motion";
import { ArrowUpRight, Linkedin, Briefcase, Compass, BarChart3, FileCheck2, Megaphone, Map as MapIcon } from "lucide-react";

// Anonymised function pillars — no names, no faces, no individual bios.
const leadership = [
  {
    icon: Compass,
    label: "Founder · Strategy",
    fn: "Strategy & Project Structuring",
    description: "Leads investment strategy design, project structuring, partner selection, and long-term portfolio direction.",
    languages: ["German", "English", "Russian"],
  },
  {
    icon: Briefcase,
    label: "Founder · Operations",
    fn: "Operations & Execution",
    description: "Leads operational coordination, project execution, partner management, and implementation quality across the full client journey.",
    languages: ["German", "English", "Turkish"],
  },
];

const functions = [
  {
    icon: BarChart3,
    label: "Investment Consulting",
    description: "Investment analysis, scenario modelling, and strategic recommendations for English-speaking clients.",
    languages: ["German", "English"],
  },
  {
    icon: Megaphone,
    label: "Marketing & Brand",
    description: "Brand strategy, campaigns, and market growth across the German and international community.",
    languages: ["German", "English", "Mandarin"],
  },
  {
    icon: FileCheck2,
    label: "Back-Office & Client Execution",
    description: "Documentation flow, client coordination, and operational support throughout the process.",
    languages: ["German", "English"],
  },
  {
    icon: BarChart3,
    label: "Investment Consulting · Growth Markets",
    description: "Scalable real estate portfolio shaping across key German growth markets.",
    languages: ["German", "English"],
  },
  {
    icon: MapIcon,
    label: "Investment Consulting · Leipzig",
    description: "Real estate consulting for expats with a regional Leipzig focus.",
    languages: ["German", "English"],
  },
  {
    icon: BarChart3,
    label: "Investment Consulting · Expats",
    description: "Dedicated real estate consulting for international and expat clients.",
    languages: ["German", "English", "French"],
  },
];

const TeamPage = () => {
  return (
    <div className="min-h-screen bg-ire-black pt-32 pb-24" data-testid="team-page">
      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-16 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">
              The Team
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight text-white leading-[1.1] mb-6">
            People behind
            <br />
            <span className="text-white/40">the process.</span>
          </h1>
          <p className="font-body text-base lg:text-lg text-white/50 max-w-2xl leading-relaxed">
            Founded 2022 in Berlin. English-speaking advisors with 20+ years combined
            experience in German real estate.
          </p>
        </motion.div>
      </div>

      {/* Leadership — anonymised featured layout */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 mb-20">
        <div className="grid lg:grid-cols-2 gap-5">
          {leadership.map((m, index) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group border border-white/[0.06] bg-ire-surface hover:border-ire-accent/30 transition-all duration-500 relative overflow-hidden"
                data-testid={`team-page-leadership-${index + 1}`}
              >
                <div className="absolute -right-16 -top-16 w-56 h-56 bg-ire-accent/[0.05] rounded-full blur-3xl pointer-events-none" />
                <div className="p-8 relative">
                  <div className="w-14 h-14 border border-ire-accent/30 flex items-center justify-center mb-6 text-ire-accent">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <p className="font-body text-[10px] tracking-[0.25em] text-ire-accent uppercase mb-3">Leadership</p>
                  <h3 className="font-heading text-2xl text-white mb-2 leading-snug">{m.label}</h3>
                  <div className="font-body text-xs text-white/40 mb-4">{m.fn}</div>
                  <p className="font-body text-sm text-white/45 leading-relaxed mb-5">{m.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {m.languages.map((lang, i) => (
                      <span key={i} className="px-3 py-1 border border-white/[0.06] font-body text-[10px] text-white/40">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Company values */}
      <div className="bg-ire-surface py-16 mb-20">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid lg:grid-cols-3 gap-5">
            {[
              { title: "Company History", text: "IRE Berlin was founded in 2022 as a Berlin-based, English-speaking real estate advisory built for high-income expats. Our partners bring two decades of execution experience across the German market to investors buying property here for the first time." },
              { title: "Mission & Philosophy", text: "Our mission is to create long-term value through sustainable real estate. We stand for integrity, transparency, and offering our clients the best opportunities. 'Wealth is in the land register.'" },
              { title: "Our Commitment", text: "We earn through project development — not brokerage fees. This ensures exclusive opportunities at market value without extra costs. Long-term relationships, not transactions." },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-ire-black border border-white/[0.06] p-8"
              >
                <h3 className="font-heading text-lg text-white mb-4">{card.title}</h3>
                <p className="font-body text-sm text-white/40 leading-relaxed">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Function grid */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
        <h2 className="font-heading text-2xl font-extralight text-white mb-10">
          Functions across the engagement
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06]">
          {functions.map((m, index) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-[#050505] p-7 group relative"
                data-testid={`team-page-function-${index + 1}`}
              >
                <div className="absolute top-0 left-0 h-px w-10 bg-ire-accent/60 group-hover:w-full transition-all duration-700" />
                <div className="w-11 h-11 border border-white/[0.08] flex items-center justify-center mb-5 text-ire-accent/80 group-hover:border-ire-accent/40 group-hover:text-ire-accent transition-colors duration-500">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg text-white mb-3 leading-snug">{m.label}</h3>
                <p className="font-body text-[12px] text-white/45 leading-relaxed mb-4">{m.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {m.languages.map((lang, i) => (
                    <span key={i} className="px-2 py-0.5 border border-white/[0.05] font-body text-[9px] text-white/30">{lang}</span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Social CTA */}
        <div className="mt-16 pt-16 border-t border-white/5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <h3 className="font-heading text-xl text-white mb-2">Follow our journey</h3>
            <p className="font-body text-sm text-white/40">Stay updated with insights, new projects, and team news.</p>
          </div>
          <div className="flex gap-4">
            <a
              href="https://www.linkedin.com/company/ire-berlin/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 border border-white/10 px-6 py-3 text-white/60 hover:border-ire-accent hover:text-ire-accent transition-all duration-500"
            >
              <Linkedin className="w-4 h-4" />
              <span className="font-body text-xs tracking-wider uppercase">LinkedIn</span>
            </a>
            <a
              href="https://www.instagram.com/ire.berlin/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 border border-white/10 px-6 py-3 text-white/60 hover:border-ire-accent hover:text-ire-accent transition-all duration-500"
            >
              <ArrowUpRight className="w-4 h-4" />
              <span className="font-body text-xs tracking-wider uppercase">Instagram</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
